import { authHeaders, Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface JobApplications {
  _id: string;
  jobId: string;
  freelancerId: string;
  freelancer:{
    profilePicture(profilePicture: any): string;
    _id:string;
    name:string;
    category:string;
    availability:boolean;
    rating:number;
    skill:string[];
    
  }
  userId: string;
  coverLetterText: string;
  durationValue:number;
  durationUnit:number;
  coverLetterFileUrl: string;
  proposedBudget: string;
  status?: string | number;
  contractId?: string | null;
}

export interface aiRecommendedFreelancerjob {
  limit: number,
  page: number,
  total: number,
  results: freelancerResponse[]
}

export interface freelancerResponse {
  freelancer: FreelancerProfile,
}

export interface FreelancerProfile {
  profilePicture: string;
  _id: string;
  fullName: string;
  name: string;
  userId: string;
  experience: number;
  phone: string;
  designation: string;
  rating: number;
  skills: string[];
  category: string;
  projectsCompleted: number;
  resume_url: string | null;
  certifications: string[];
  achievements: string[];
  dailyRate: number;
  totalEarnings: number;
  bio: string;
  availability: boolean;
  socialMediaLinks: {
    _id: string;
  }[];
  education: Education[];
  project: any;
  location: string;
  createdAt: string;
  updatedAt: string;
}

interface Education {
  degree: string;
  institution: string;
  year: string;
}

export interface JobDetail {
  job:{
    PricingDetails: string;
    budget:number;
    category:string;
    description:string;
    duration:string;
    durationValue?:number;
    durationUnit?:number;
    projectvisibility:boolean;
    requiredSkills:string[];
    title:string
    _id:string
    createdAt:string;
  };
  status: number;
  enterprise:{
    companyName:string;
    reviews: any;
    logo(logo: any): string;
    _id:string;
    name:string;
    location:string;
    rating: number
  };
  application?:{
    coverLetterFileUrl?:string;
    status?:number;
    proposedBudget?: number;
    _id?:string;
  }
}

interface JobState {
  jobApplications: JobApplications[];
  aiRecommendedFreelancerjob: aiRecommendedFreelancerjob
  jobdetails: JobDetail | null
  isLoading: boolean;
  error: string | null;
  fetchJobApplications: (id: string, token: string) => Promise<void>;
  fetchAIRecommendedFreelancerJob: (id: string, token: string, limit: number, pageNumber: number) => Promise<void>;
  fetchJobDetailsByFreelancer: (jobId:string, userId: string, token: string, params?: {
    search?: string;
    statud?: boolean | number;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  updateApplicationStatus: (id: string, data: number, token: string) => Promise<void>;
  clearJobProfile: () => void;
}

export const useJobApplyStore = create<JobState>()(
  persist(
    (set) => ({
      jobApplications: null,
      aiRecommendedFreelancerjob: null,
      jobdetails: null,
      userJobs: null,
      isLoading: false,
      error: null,

      fetchJobApplications: async (id, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_JOB_APPLICATIONS}/${id}`,
            authHeaders(token)
          );
          set({ jobApplications: res?.data?.results, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to fetch job applications",
            isLoading: false,
          });
        }
      },

      fetchAIRecommendedFreelancerJob: async (id, token, limit, pageNumber) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_AI_RECOMMENDED_FREELANCER_JOB}/${id}?page=${pageNumber}&limit=${limit}`,
            authHeaders(token)
          );
          set({ aiRecommendedFreelancerjob: res?.data, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to fetch job AI Recommended Freelancer",
            isLoading: false,
          });
        }
      },

      fetchJobDetailsByFreelancer: async (jobId, userId, token, params={}) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.get(
            `${API_END_POINTS.GET_JOB_DETAILS_BY_FREELANCER}/${jobId}/${userId}`,
            authHeaders(token)
          );
          set({ jobdetails: res.data, isLoading: false });
        } catch (err: any) {
          set({
            error: err.message || "Failed to fetch job applications",
            isLoading: false,
          });
        }
      },

      updateApplicationStatus: async (id,data, token) => {
        set({ isLoading: true, error: null });
        try {
          const res = await Axios.put(
            `${API_END_POINTS.UPDATE_JOB_APPLICATION_STATUS}/${id}`,
            {status: data},
            authHeaders(token)
          );
          set((state) => {
            if (!state.jobApplications || !Array.isArray(state.jobApplications)) return state;

            const updatedIndex = state.jobApplications.findIndex(
              (application) => application?._id === id
            );
            if (updatedIndex === -1) return state;

            const updatedApplications = [...state.jobApplications];
            updatedApplications[updatedIndex] = {
              ...updatedApplications[updatedIndex],
              status: data,
            };

            return { jobApplications: updatedApplications, isLoading: false };
          });
        } catch (err: any) {
          set({
            error: err.message || "Failed to update job applications status",
            isLoading: false,
          });
        }
      },

      clearJobProfile: () =>
        set({ jobApplications: null, isLoading: false, error: null }),
    }),
    {
      name: "job-apply-storage",
    }
  )
);
